import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './parts-page.style.js';
import { getPartsByCompany, getAssetsByCompany } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { Part, PartFormData } from '../../../types/part.types.js';
import { createPartsTableConfig } from './parts-table-config.js';
import { SubscriptionPlanCat } from '@cmms/models';
import '../../common/upgrade-placeholder/upgrade-placeholder.js';
import '../../common/upgrade-modal/upgrade-modal.js';

@customElement('parts-page')
export class PartsPage extends LitElement {
  @property({ type: String })
  companyId = '';
  
  @property({ type: Object })
  firestore: Firestore = null as any;

  @property({ type: Number })
  subscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;

  @state() private parts: Part[] = [];
  @state() private showUpgradeModal = false;
  @state() private upgradeContext = { action: '', feature: '' };
  @state() private assets: Array<{ id: string; model?: { name: string } }> = [];
  @state() private isLoading = true;
  @state() private error: string | null = null;
  @state() private showAddModal = false;
  @state() private selectedPart: Part | null = null;
  @state() private formData: PartFormData = {
    name: '',
    description: '',
    stock_qty: 0,
    min_qty: 0,
    max_qty: 0,
    reorder_level: 0,
    cost: 0,
    asset_id: ''
  };
  private cachedTableConfig: any = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('companyId')) {
      this.loadData();
    }
  }

  private hasInventoryAccess(): boolean {
    // Professional (2) and Enterprise (3) have access, Starter (1) does not
    return this.subscriptionPlan >= SubscriptionPlanCat.PROFESSIONAL;
  }

  private handleUpgradeRequest(event: CustomEvent) {
    this.upgradeContext = {
      action: event.detail.action || 'Access',
      feature: 'Inventory Management'
    };
    this.showUpgradeModal = true;
  }

  private handleAddPartClick() {
    if (!this.hasInventoryAccess()) {
      this.upgradeContext = {
        action: 'Add Parts',
        feature: 'Inventory Management'
      };
      this.showUpgradeModal = true;
      return;
    }
    this.handleAddPart();
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.error = null;

      const [partsResult, assetsResult] = await Promise.all([
        getPartsByCompany(this.firestore, this.companyId),
        getAssetsByCompany(this.firestore, this.companyId)
      ]);
      
      if (partsResult.error) {
        this.error = 'Failed to load parts';
        console.error('Error loading parts:', partsResult.error);
      } else {
        this.parts = partsResult.data || [];
      }

      if (assetsResult.data) {
        this.assets = assetsResult.data;
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.error = 'Failed to load data';
    } finally {
      this.isLoading = false;
    }
  }

  private handleRowClick = (part: Part) => {
    this.selectedPart = part;
  };


  private handleFilterChange = (filters: Record<string, any>) => {
    // TODO: Implement filter functionality
  };

  private handleSort = (column: string, direction: 'asc' | 'desc') => {
    // TODO: Implement sort functionality
  };

  private formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }

  private handleAddPart = () => {
    this.showAddModal = true;
  }

  private handleModalClosed() {
    this.showAddModal = false;
    this.formData = {
      name: '',
      description: '',
      stock_qty: 0,
      min_qty: 0,
      max_qty: 0,
      reorder_level: 0,
      cost: 0,
      asset_id: ''
    };
  }

  private handlePartCreated(event: CustomEvent) {
    const newPart = event.detail;
    this.parts = [...this.parts, newPart];
    this.showAddModal = false;
    this.formData = {
      name: '',
      description: '',
      stock_qty: 0,
      min_qty: 0,
      max_qty: 0,
      reorder_level: 0,
      cost: 0,
      asset_id: ''
    };
  }

  private handleViewPart(part: Part) {
    this.selectedPart = part;
  }

  private handleViewerClosed() {
    this.selectedPart = null;
  }

  private handlePartUpdated(event: CustomEvent) {
    const updatedPart = event.detail;
    // Update the part in the local array
    this.parts = this.parts.map(part => 
      part.id === updatedPart.id ? updatedPart : part
    );
    this.selectedPart = null; // Close the viewer
  }

  render() {
    // Show upgrade placeholder for Basic plan users
    if (!this.hasInventoryAccess()) {
      return html`
        <div class="parts-page">
          <upgrade-placeholder
            featureName="Inventory Management"
            description="Take control of your spare parts and inventory with our comprehensive management system. Track stock levels, set reorder points, and never run out of critical parts again."
            icon="boxes"
            .currentPlan=${this.subscriptionPlan}
            .benefits=${[
              'Unlimited parts and inventory tracking',
              'Automatic reorder notifications',
              'Parts usage analytics and reporting',
              'Integration with work orders and assets',
              'Barcode scanning support',
              'Vendor management and purchase orders'
            ]}
            @upgrade-requested=${this.handleUpgradeRequest}
          ></upgrade-placeholder>

          <!-- Upgrade Modal -->
          <upgrade-modal
            ?open=${this.showUpgradeModal}
            featureName=${this.upgradeContext.feature}
            actionName=${this.upgradeContext.action}
            @upgrade-confirmed=${this.handleUpgradeRequest}
            @sl-request-close=${() => this.showUpgradeModal = false}
          ></upgrade-modal>
        </div>
      `;
    }

    // Create table config with caching for Professional/Enterprise users
    if (!this.cachedTableConfig || 
        this.cachedTableConfig.data !== this.parts ||
        this.cachedTableConfig.loading !== this.isLoading ||
        this.cachedTableConfig.error !== this.error) {
      
      this.cachedTableConfig = createPartsTableConfig(
        this.parts,
        this.assets,
        {
          onRowClick: this.handleRowClick,
          onAddPart: this.handleAddPartClick, // Use the wrapper method
          onFilterChange: this.handleFilterChange,
          onSort: this.handleSort
        }
      );

      // Override loading and error state
      this.cachedTableConfig.loading = this.isLoading;
      this.cachedTableConfig.error = this.error;
    }

    const tableConfig = this.cachedTableConfig;

    return html`
      <div class="parts-page">
        <!-- Data Table -->
        <data-table .config=${tableConfig}></data-table>

        <!-- Add Part Modal -->
        <part-modal
          .open=${this.showAddModal}
          .formData=${this.formData}
          .companyId=${this.companyId}
          .firestore=${this.firestore}
          @modal-closed=${this.handleModalClosed}
          @part-created=${this.handlePartCreated}
        ></part-modal>
        
        <!-- Part Viewer Modal -->
        <part-viewer
          .open=${this.selectedPart !== null}
          .part=${this.selectedPart}
          @modal-closed=${this.handleViewerClosed}
          @part-updated=${this.handlePartUpdated}
        ></part-viewer>

        <!-- Upgrade Modal -->
        <upgrade-modal
          ?open=${this.showUpgradeModal}
          featureName=${this.upgradeContext.feature}
          actionName=${this.upgradeContext.action}
          @upgrade-confirmed=${this.handleUpgradeRequest}
          @sl-request-close=${() => this.showUpgradeModal = false}
        ></upgrade-modal>
      </div>
    `;
  }

  static styles = styles;
}

declare global {
  interface HTMLElementTagNameMap {
    'parts-page': PartsPage;
  }
}
